import { useState, useEffect } from 'react';
import axios from '../../../../utils/axios';
import { toastSuccess, toastError } from '../../../../utils/toast';

// Display a YYYY-MM-DD date string in the user's locale without timezone shift.
// Anchoring at noon avoids the off-by-one that midnight UTC causes when the
// browser converts to local time (same fix as PP-404).
function fmtDate(d) {
    return new Date(`${d}T12:00:00`).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    });
}

function today() {
    return new Date().toISOString().slice(0, 10);
}

export default function BlackoutPeriodEditor({ listing, readOnly = false }) {
    const propertyId = listing.propertyId;

    const [periods, setPeriods]     = useState([]);
    const [loadErr, setLoadErr]     = useState(null);
    const [loading, setLoading]     = useState(true);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate]     = useState('');
    const [reason, setReason]       = useState('');

    const [conflicts, setConflicts] = useState(null); // { hasConflicts, conflicts[] }
    const [formErr, setFormErr]     = useState(null);
    const [saving, setSaving]       = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        if (!propertyId) { setLoading(false); return; }
        axios.get(`/booking/${propertyId}/availability`)
            .then(res => setPeriods(res.data.availabilityModel?.blackoutPeriods || []))
            .catch(() => setLoadErr('Failed to load blackout periods.'))
            .finally(() => setLoading(false));
    }, [propertyId]);

    const resetForm = () => {
        setStartDate('');
        setEndDate('');
        setReason('');
        setConflicts(null);
        setFormErr(null);
    };

    const handleAdd = async () => {
        setFormErr(null);
        setConflicts(null);
        setLoadErr(null);
        setSaving(true);
        try {
            const res = await axios.post(`/property/${propertyId}/blackout`, {
                startDate,
                endDate,
                ...(reason ? { reason } : {}),
            });
            setPeriods(res.data.availability.blackoutPeriods);
            resetForm();
            toastSuccess("Blackout dates saved. Drivers won't be able to book this property during the selected dates.");
        } catch (err) {
            if (err.response?.status === 409) {
                setConflicts(err.response.data);
            } else {
                setFormErr(err.response?.data?.message || 'Failed to add blackout period.');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (blackoutId) => {
        setDeletingId(blackoutId);
        setLoadErr(null);
        try {
            const res = await axios.delete(`/property/${propertyId}/blackout/${blackoutId}`);
            setPeriods(res.data.availability.blackoutPeriods);
            toastSuccess('Blackout period removed. Drivers can now book these dates again.');
        } catch {
            toastError('Failed to remove blackout period.');
        } finally {
            setDeletingId(null);
        }
    };

    const overlapsExisting = startDate && endDate && startDate <= endDate && periods.some(
        p => startDate <= p.endDate && endDate >= p.startDate,
    );

    const canAdd = startDate && endDate && startDate <= endDate && !saving;

    return (
        <div className="host-panel-section">
            <div className="host-panel-section-label">Blackout Dates</div>
            <p className="blackout-description">
                Close specific date ranges (e.g. renovation, holiday) without pausing your listing.
                New bookings will be blocked for these dates.
            </p>

            {loadErr && <div className="drawer-field-error">{loadErr}</div>}

            {/* Existing periods */}
            {loading ? (
                <div className="blackout-loading">Loading…</div>
            ) : periods.length > 0 ? (
                <ul className="blackout-periods-list">
                    {periods.map(p => (
                        <li key={p._id} className="blackout-period-row">
                            <div className="blackout-period-info">
                                <span className="blackout-period-range">
                                    {fmtDate(p.startDate)}
                                    {p.startDate !== p.endDate && (
                                        <> &rarr; {fmtDate(p.endDate)}</>
                                    )}
                                </span>
                                {p.reason && (
                                    <span className="blackout-period-reason">{p.reason}</span>
                                )}
                            </div>
                            {!readOnly && (
                                <button
                                    type="button"
                                    className="blackout-remove-btn"
                                    onClick={() => handleDelete(p._id)}
                                    disabled={deletingId === p._id}
                                >
                                    {deletingId === p._id ? 'Removing…' : 'Remove'}
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="blackout-empty">No blackout periods set.</p>
            )}

            {/* Add form — hidden in read-only (view) mode */}
            {!readOnly && <div className="blackout-add-form">
                <div className="blackout-date-row">
                    <label className="blackout-date-field">
                        <span className="blackout-date-label">From</span>
                        <input
                            type="date"
                            className="blackout-date-input"
                            value={startDate}
                            min={today()}
                            onChange={e => { setStartDate(e.target.value); setConflicts(null); }}
                        />
                    </label>
                    <label className="blackout-date-field">
                        <span className="blackout-date-label">To</span>
                        <input
                            type="date"
                            className="blackout-date-input"
                            value={endDate}
                            min={startDate || today()}
                            onChange={e => { setEndDate(e.target.value); setConflicts(null); }}
                        />
                    </label>
                </div>

                <input
                    type="text"
                    className="blackout-reason-input"
                    placeholder="Reason (optional)"
                    value={reason}
                    maxLength={200}
                    onChange={e => setReason(e.target.value)}
                />

                {overlapsExisting && !conflicts && (
                    <div className="blackout-overlap-warning">
                        <p>A blackout period already covers some or all of these dates.</p>
                    </div>
                )}

                {conflicts?.conflicts?.length > 0 && (
                    <div className="blackout-conflict-banner">
                        <p>This property has {conflicts.conflicts.length} active reservation{conflicts.conflicts.length !== 1 ? 's' : ''} during the selected dates. Please cancel or adjust the blackout period before setting dates as unavailable.</p>
                    </div>
                )}

                {formErr && <div className="drawer-field-error">{formErr}</div>}

                <button
                    type="button"
                    className="btn btn-secondary blackout-add-btn"
                    onClick={handleAdd}
                    disabled={!canAdd}
                >
                    {saving ? 'Saving…' : 'Add Blackout Period'}
                </button>
            </div>}
        </div>
    );
}
